import React, { useState, useEffect } from 'react';
import { red } from '@mui/material/colors';
import { DialogContent, DialogTitle, Grid, Typography, Collapse, RadioGroup, Radio } from '@mui/material';
import { FormControlLabel, Chip, TextField, FormControl, useTheme, Button, useMediaQuery } from '@mui/material';
import { PersonAddRounded, DeleteOutlineRounded } from '@mui/icons-material';
import { RoundedDialog, GeneralButton, StandardDialogActions, UserChip } from '../../../../Global/Components';
import { GroupChip, TagsSelector } from '../../../../Global/Components';
import { Alert, AlertTitle } from '@mui/material';
import StateManager from '../../../../Global/StateManager';
import { useHistory } from 'react-router-dom';
import axios from 'axios';

export default function WorkspaceDialog({ open, onClose, onResult, initial }) {
  // setings themselves
  const [title, setTitle] = useState('');
  const [accessType, setAccessType] = useState('private');
  const [accessList, setAccessList] = useState([]);
  const [accessGroups, setAccessGroups] = useState([]);
  const [tags, setTags] = useState([]);

  const [saving, setSaving] = useState(false);
  const theme = useTheme();

  const largeDevices = useMediaQuery(theme.breakpoints.up('sm'));
  const userId = localStorage.getItem('_id');
  const history = useHistory();

  useEffect(() => {
    if (!open) return;
    setTitle(initial?.title || '');
    setAccessType(initial?.accessType || 'private');
    setAccessList(initial?.accessList || []);
    setAccessGroups(initial?.accessGroups || []);
    setTags(initial?.tags || []);
  }, [open]); // eslint-disable-line

  function saveAccessUsers(result) {
    if (result.users) {
      setAccessList(result.users);
    }

    if (result.groups) {
      setAccessGroups(result.groups);
    }
  }

  function selectAccessUsers() {
    StateManager.selectMultipleUsers(saveAccessUsers, {
      initiallySelected: accessList,
      initiallySelectedGroups: accessGroups,
      showGroups: true,
    });
  }

  function save() {
    const body = {
      workspaceId: initial?._id,
      title,
      accessType,
      accessList: accessType === 'private' ? accessList : [],
      accessGroups: accessType === 'private' ? accessGroups : [],
      tags: tags.map((x) => x._id),
    };
    setSaving(true);

    axios
      .post(`/workhub/workspace/${initial ? 'saveWorkspaceSettings' : 'createWorkspace'}`, body)
      .then((res) => {
        StateManager.setSuccessAlert('Workspace has been created');
        setSaving(false);
        onResult(res.data);
        onClose();
      })
      .catch((err) => {
        setSaving(false);
        StateManager.setAxiosErrorAlert(err);
      });
  }

  function addTag(tag) {
    if (tags.find((x) => x._id === tag._id)) return;
    setTags([...tags, tag]);
  }

  function removeTag(id) {
    setTags(tags.filter((x) => x._id !== id));
  }

  function confirmDelete() {
    StateManager.setConfirm('You are about to delete this workspace', deleteWorkspace);
  }

  function deleteWorkspace() {
    setSaving(true);
    axios
      .post('/workhub/workspace/deleteWorkspace', { workspaceId: initial?._id })
      .then(() => {
        StateManager.setSuccessAlert('Workspace has been deleted');
        setSaving(false);
        history.push('/tasks/workspaces');
      })
      .catch((err) => {
        setSaving(false);
        StateManager.setAxiosErrorAlert(err);
      });
  }

  return (
    <RoundedDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      className="scroll-bar"
      fullScreen={!largeDevices}
    >
      <DialogTitle>{initial ? 'Workspace settings' : 'New workspace'}</DialogTitle>
      <DialogContent>
        <Grid container item sx={{ mt: 1 }}>
          <TextField
            value={title}
            label="Workspace title"
            variant="outlined"
            fullWidth
            onChange={(e) => setTitle(e.target.value)}
            inputProps={{ maxLength: 64 }}
          />
        </Grid>
        <Grid container item sx={{ my: 1, py: 1 }}>
          <Grid container item>
            <Typography gutterBottom variant="h6">
              Access:
            </Typography>
          </Grid>
          <Grid container item>
            <FormControl component="fieldset" variant="standard">
              <RadioGroup row value={accessType} onChange={(e) => setAccessType(e.target.value)}>
                <FormControlLabel value="private" control={<Radio color="primary" />} label="Private" />
                <FormControlLabel value="public" control={<Radio color="primary" />} label="Public" />
              </RadioGroup>
            </FormControl>
          </Grid>
          <Collapse in={accessType === 'private'} style={{ width: '100%' }}>
            <Grid container item direction="column">
              <Typography gutterBottom color="textSecondary">
                Users who have access:
              </Typography>
              <Grid container item alignItems="center">
                <Button variant="outlined" startIcon={<PersonAddRounded />} onClick={selectAccessUsers}>
                  Add users and groups
                </Button>
                {accessList.map((user) => (
                  <UserChip
                    key={user}
                    margin={8}
                    id={user}
                    onDelete={() => setAccessList(accessList.filter((x) => x !== user))}
                  />
                ))}
                {accessGroups.map((group) => (
                  <GroupChip
                    key={group}
                    id={group}
                    margin={8}
                    onDelete={() => setAccessGroups(accessGroups.filter((x) => x !== group))}
                  />
                ))}
              </Grid>
            </Grid>
          </Collapse>
        </Grid>

        <Grid container item sx={{ my: 1 }}>
          <Grid container item>
            <Typography gutterBottom variant="h6">
              Tags
            </Typography>
          </Grid>
          <Grid container item alignItems="center">
            <TagsSelector value={tags} onChange={setTags} returnObjects />
            {tags.map((tag) => (
              <Chip
                key={tag._id}
                label={tag.text}
                style={{
                  background: tag.color,
                  color: theme.palette.getContrastText(tag.color),
                  margin: 8,
                }}
                onDelete={() => removeTag(tag._id)}
              />
            ))}
          </Grid>
        </Grid>

        {initial && initial.createdBy === userId && !initial.isDefault && (
          <Grid container item tyle={{ margin: '1rem 0' }}>
            <Grid container item>
              <Alert severity="error" style={{ width: '100%', marginBottom: 16, borderRadius: 8 }}>
                <AlertTitle>Danger zone</AlertTitle>
                The below actions are potentially dangerous and may result in data loss
              </Alert>
            </Grid>
            <Grid container item>
              <GeneralButton style={{ color: red[500] }} startIcon={<DeleteOutlineRounded />} onClick={confirmDelete}>
                Delete workspace
              </GeneralButton>
            </Grid>
          </Grid>
        )}
      </DialogContent>
      <StandardDialogActions saving={saving} onClose={onClose} onDone={save} />
    </RoundedDialog>
  );
}
